/**
 * Single source of truth for everything race-specific.
 *
 * The site, the registration form, the server-side price check and the
 * confirmation email all read from this file, so most race changes are a
 * one-line edit here. Values marked PLACEHOLDER are sample content that the
 * race director still needs to confirm; DRAFT text about Kurt needs the
 * family's approval before anything is published.
 */

export type EventId = '5k'

export type PriceTier = {
  label: string
  /** ISO timestamp (with offset) when this price stops applying. Omit for the final tier. */
  endsAt?: string
  cents: number
}

export type RaceEvent = {
  id: EventId
  name: string
  shortName: string
  blurb: string
  distanceKm: number
  startTime: string // local HH:MM on race day
  timed: boolean
  includes: string[]
  /** Age on race day. */
  minAge?: number
  maxAge?: number
  capacity: number
  /** One tier means one flat price. */
  tiers: PriceTier[]
  enabled: boolean
}

export type ShirtFit = 'unisex' | 'womens' | 'youth' | 'none'

export type SupportResource = { name: string; how: string; href: string }

export type RaceConfig = {
  name: string
  shortName: string
  /** Prefix for confirmation codes, e.g. KK-7Q4M2X. */
  codePrefix: string
  edition: string
  tagline: string
  startsAt: string
  timezone: string
  /** When online registration opens; null while the date is still to be announced. */
  registrationOpensAt: string | null
  registrationClosesAt: string
  shirtGuaranteeAt: string
  refundDeadlineAt: string
  capacity: number
  location: {
    venue: string
    formalName: string
    city: string
    region: string
    regionCode: string
    areaLine: string
    startArea: string
    lat: number
    lon: number
    mapsUrl: string
    directionsUrl: string
    operator: string
  }
  organizer: { name: string; email: string }
  /** The person the race remembers. */
  memorial: {
    name: string
    /** Short line used on bibs and in the hero, e.g. "Running for Kurt". */
    dedication: string
    /** What he was, in a few words each; shown as a list. */
    traits: { title: string; detail: string }[]
    /** Paragraphs for the "Why we run" section. */
    story: string[]
    /** Optional photo URL (put the file in /public). */
    photo?: string
  }
  cause: string
  beneficiary: { name: string; blurb: string }
  fundraising: { goalCents: number }
  supportResources: SupportResource[]
  currency: 'usd'
  processingFee: { mode: 'pass' | 'absorb'; percent: number; fixedCents: number }
  events: RaceEvent[]
  extras: { extraShirtCents: number; donationPresetsCents: number[]; giftPresetsCents: number[] }
  shirts: {
    color: string
    fits: { id: Exclude<ShirtFit, 'none'>; label: string; sizes: string[] }[]
    chart: Record<string, Record<string, [number, number]>>
    youthAges: Record<string, string>
  }
  schedule: { time: string; title: string; detail: string }[]
  packetPickup: { day: string; hours: string; place: string }[]
  waiverVersion: string
  /** Tiers in the order they're shown, biggest first. */
  sponsorTiers: string[]
  /** Add each sponsor here once confirmed; the home page shows them by tier. Logos go in /public/sponsors/. */
  sponsors: { name: string; tier: string; logo?: string; url?: string }[]
}

export const race: RaceConfig = {
  name: 'Kurt’s Kicks',
  shortName: 'Kurt’s Kicks',
  codePrefix: 'KK',
  edition: 'Inaugural edition',
  tagline: 'A 5K in memory of Kurt, raising money for suicide prevention.',
  /** Race-day start, local time (America/Chicago). */
  startsAt: '2027-07-10T09:00:00-05:00',
  timezone: 'America/Chicago',
  /** Online registration opens. Not open yet: set the date (e.g. '2027-02-01T09:00:00-06:00') once it's announced. */
  registrationOpensAt: null,
  /** Online registration closes. PLACEHOLDER */
  registrationClosesAt: '2027-07-08T23:59:00-05:00',
  /** Shirt sizes guaranteed for registrations before this. PLACEHOLDER */
  shirtGuaranteeAt: '2027-06-10T23:59:00-05:00',
  /** Refunds allowed until. PLACEHOLDER */
  refundDeadlineAt: '2027-06-30T23:59:00-05:00',
  capacity: 600,

  location: {
    venue: 'Busse Woods',
    formalName: 'Ned Brown Forest Preserve',
    city: 'Elk Grove Village',
    region: 'Illinois',
    regionCode: 'IL',
    areaLine: 'Elk Grove Village, Illinois',
    startArea: 'Groves 5–11, Busse Forest South',
    /** Busse Forest South parking by Grove 5 (entrance off Higgins Road). */
    lat: 42.02758,
    lon: -87.99158,
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=42.02758%2C-87.99158',
    directionsUrl: 'https://www.google.com/maps/dir/?api=1&destination=42.02758%2C-87.99158',
    operator: 'Forest Preserves of Cook County',
  },

  organizer: {
    name: 'Kurt’s Kicks',
    email: 'kurtskicks13@gmail.com',
  },

  // DRAFT: every word about Kurt needs the family's approval before publishing.
  memorial: {
    name: 'Kurt',
    dedication: 'Running for Kurt',
    traits: [
      { title: 'Runner', detail: 'Running was part of who he was. This race keeps him moving.' },
      { title: 'Gardener', detail: 'Ran his own landscaping business and loved designing gardens.' },
      { title: 'Redbird and Illini fan', detail: 'An Illinois State grad who cheered on the Illini for his daughters.' },
      {
        title: 'Father, Husband, Brother and Friend',
        detail:
          'Kurt loved nothing more than his family and friends. You could find him jetting off to visit them on any given weekend, or cheering on his daughters at their sporting events on the others.',
      },
    ],
    story: [
      'Kurt was a runner, a landscaper and a garden designer, an Illinois State grad and an Illini fan for his daughters. More than anything, he was a force of nature, with an energy that filled any room he walked into.',
      'We lost Kurt to suicide. His daughter started this race to celebrate the way he lived, outdoors and at full speed, and to raise money for suicide prevention so that more people get help in time.',
    ],
  },

  cause: 'suicide prevention and mental health',

  beneficiary: {
    name: 'nonprofit organizations focused on suicide prevention and mental health', // PLACEHOLDER: partners to be announced
    blurb: 'All proceeds from Kurt’s Kicks will support nonprofit organizations focused on suicide prevention and mental health.',
  },

  /** Donations only (registration fees are not counted). PLACEHOLDER goal */
  fundraising: { goalCents: 1_000_000 },

  supportResources: [
    { name: '988 Suicide & Crisis Lifeline', how: 'Call or text 988, or chat online', href: 'https://988lifeline.org' },
    { name: 'Crisis Text Line', how: 'Text HOME to 741741', href: 'https://www.crisistextline.org' },
  ],

  currency: 'usd',

  /**
   * Card processing. "pass" adds Stripe's fee on top so the race keeps the full
   * entry fee (common on RunSignup-style sites). "absorb" hides it.
   */
  processingFee: { mode: 'pass', percent: 2.9, fixedCents: 30 },

  events: [
    {
      id: '5k',
      name: '5K Run/Walk',
      shortName: '5K',
      blurb: 'Chip-timed 5K on the paved trails of Busse Woods. Runners, walkers and strollers all welcome.',
      distanceKm: 5,
      startTime: '09:00',
      timed: true,
      includes: ['Chip timing', 'Purple race shirt', 'Finisher medal', 'Post-race snacks'],
      minAge: 5,
      capacity: 600,
      tiers: [{ label: 'Registration', cents: 5000 }], // PLACEHOLDER price
      enabled: true,
    },
  ],

  extras: {
    extraShirtCents: 1500,
    /** Suggested add-on donations during registration. */
    donationPresetsCents: [1000, 2500, 5000, 10000],
    /** Suggested gifts on the Donate page. */
    giftPresetsCents: [2500, 5000, 10000, 25000],
  },

  shirts: {
    color: 'purple',
    fits: [
      { id: 'unisex', label: 'Unisex', sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'] },
      { id: 'womens', label: 'Women’s fit', sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL'] },
      { id: 'youth', label: 'Youth', sizes: ['YXS', 'YS', 'YM', 'YL'] },
    ],
    /** Chest circumference (body measurement), cm. Sample chart until the supplier confirms. */
    chart: {
      unisex: { XS: [81, 86], S: [86, 91], M: [97, 102], L: [107, 112], XL: [117, 122], '2XL': [127, 132], '3XL': [137, 142] },
      womens: { XS: [76, 81], S: [81, 86], M: [89, 94], L: [97, 102], XL: [104, 109], '2XL': [112, 117] },
      youth: { YXS: [56, 61], YS: [61, 66], YM: [66, 71], YL: [71, 76] },
    },
    youthAges: { YXS: '2–4', YS: '6–8', YM: '10–12', YL: '14–16' },
  },

  /** Race-day timeline (local time). PLACEHOLDER */
  schedule: [
    { time: '07:30', title: 'Check-in and bib pickup open', detail: 'In the event area at Groves 5–11. Bring your confirmation code or photo ID.' },
    { time: '08:30', title: 'Group warm-up', detail: 'Wear purple. Led by a local running coach in the event area.' },
    { time: '08:50', title: 'A moment for Kurt', detail: 'A few words and a short pause together before the start.' },
    { time: '09:00', title: '5K start', detail: 'On the Red Paved Loop beside Grove 5. Chip time starts when you cross the mat.' },
    { time: '10:30', title: 'Course closes', detail: '90-minute time limit; the sweep walks in with the last finisher.' },
  ],

  /** Race morning only. PLACEHOLDER hours */
  packetPickup: [{ day: '2027-07-10', hours: '07:30–08:45', place: 'Event area at Groves 5–11, Busse Forest South' }],

  waiverVersion: '2026-10-03',

  sponsorTiers: ['Presenting partner', 'Course partners', 'Community friends'],
  // e.g. { name: 'Example Co.', tier: 'Course partners', logo: '/sponsors/example-co.png', url: 'https://example.com' }
  sponsors: [],
}

export const enabledEvents = race.events.filter((e) => e.enabled)

export type RegistrationPhase = 'upcoming' | 'open' | 'closed'

/** Where online registration stands: not open yet (no date set counts as not open), open, or closed. */
export function registrationPhase(now: Date = new Date()): RegistrationPhase {
  if (!race.registrationOpensAt || now.getTime() < new Date(race.registrationOpensAt).getTime()) return 'upcoming'
  return now.getTime() <= new Date(race.registrationClosesAt).getTime() ? 'open' : 'closed'
}
export const getEvent = (id: string) => race.events.find((e) => e.id === id)
/** The race has a single event now; most of the UI talks about "the 5K". */
export const mainEvent = race.events[0]
